import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  DoCheck,
} from "@angular/core";
import { ProjectService } from "../../services/project/project.service";
import { FeatureService } from "../../services/feature/feature.service";
import { Project } from "../../interfaces/project";
import { Feature } from "../../interfaces/feature";
import { ActivatedRoute } from "@angular/router";
import { Epic } from "../../interfaces/epic";
import { EpicService } from "../../services/epic/epic.service";
import { JiraService } from "../../services/jira/jira.service";
import { environment } from "../../../environments/environment";
import { MzModalService, MzToastService } from "ng2-materialize";
import { ConfirmSyncComponent } from "../confirm-sync/confirm-sync.component";
import { DeleteItemConfirmationDialogComponent } from "../delete-item-confirmation-dialog/delete-item-confirmation-dialog.component";
import { Utils } from "../../utils/utils";
import { EditItemDialogComponent } from "../edit-item-dialog/edit-item-dialog.component";
import { EditThemeDialogComponent } from "../edit-theme-dialog/edit-theme-dialog.component";
import { PlanningPokerService } from "../../services/planning-poker/planning-poker.service";
import { ExportService } from "../../services/export/export.service";
import { saveAs } from "file-saver";
import { SprintService } from "../../services/sprint/sprint.service";
import { Observable } from "../../../../node_modules/rxjs";
import { EditSprintDialogComponent } from "../edit-sprint-dialog/edit-sprint-dialog.component";
import { Sprint } from "../../interfaces/sprint";
import { JiraSyncProgressComponent } from "../jira-sync-progress/jira-sync-progress.component";
import { chartColorScheme } from "../../utils/chartColortScheme";

@Component({
  selector: "app-feature-canvas",
  templateUrl: "./feature-canvas.component.html",
  styleUrls: ["./feature-canvas.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeatureCanvasComponent implements OnInit {
  readonly env: any = environment;
  readonly utils: any = Utils;
  readonly sortableOptions = {
    group: {
      name: "features",
      pull: true,
      put: true,
    },
    onUpdate: async (event: any) => {
      await this.setNewIndex(event);
    },
    onMove: (evt: any, originalEvent: any) => {
      /**
       * if y-coordinate of event is <=200, scroll to top of page
       */
      if (originalEvent.clientY.valueOf() <= 200) {
        $("html, body").animate(
          {
            scrollTop: 0,
          },
          "slow"
        );
      }
    },
    ghostClass: "ghost",
    animation: 150,
    scroll: true,
  };

  // options for average chart

  bars: any[] = [];
  lines: any[] = [];

  showXAxis = true;
  showYAxis = true;
  gradient = false;
  showLegend = true;
  showXAxisLabel = true;
  xAxisLabelBar = "User Stories";
  showYAxisLabel = true;
  yAxisLabelBar = "Story Points Einigung";
  colorScheme = chartColorScheme;
  legendTitle = "Legende";

  showRightYAxisLabel: boolean = true;
  yAxisLabelRight: string = "Streuung";

  schemeType = "ordinal";

  showBars: boolean = true;
  checkboxes: boolean[] = [false, false, false, false]; //defines wich spread checkboxes are selected by default
  checkboxesDisabled: boolean[] = [false, false, false, false];
  radio: number = 1; // 1 = average; 2 = median; 3 = definedValue
  m = 0; //slope of trendline
  b = 0; // beginning of trendline

  yMax = 100;
  chartWidth = "100%";
  //  chartWidth= undefined;

  barColor = {
    group: "ordinal",
    domain: ["#d6e098"],
  };
  lineColors = {
    group: "ordinal",
    domain: ["#d6e098", "#90535d", "#686a6e", "#497c88"],
  };

  hasEstimatedItems = false;

  agreementAverage: number = 0;
  agreementMedian: number = 0;
  agreementValues: number[] = [];

  @ViewChild("EditItemModal") EditItemModal: EditItemDialogComponent;
  @ViewChild("EditThemeModal") EditThemeModal: EditThemeDialogComponent;
  @ViewChild("DeleteItemConfirmationModal")
  DeleteItemConfirmationModal: DeleteItemConfirmationDialogComponent;
  @ViewChild("JiraSyncProgressModal")
  JiraSyncProgressModal: JiraSyncProgressComponent;

  loading: boolean;
  project: Project;
  sprint: any;
  epics: Epic[];
  sprints: Sprint[];
  allFeatures: Feature[];

  newFeature = {} as Feature;
  newEpic = { color: Utils.presetThemeColors[0][0] } as Epic;
  editEpicTitle: string;
  epicFilterName = "Nach Themes filtern" as string;
  selectedThemes: Array<String> = [];
  carouselItemIndex: Number = -1;

  constructor(
    private projectService: ProjectService,
    public modalService: MzModalService,
    private planningPokerService: PlanningPokerService,
    private changeDetector: ChangeDetectorRef,
    private toastService: MzToastService,
    private featureService: FeatureService,
    private epicService: EpicService,
    private route: ActivatedRoute,
    private jiraService: JiraService,
    private exportService: ExportService,
    private sprintService: SprintService
  ) {}

  ngOnInit() {
    /**
     * get all epics and feature by given projectID
     */
    this.route.params.subscribe(
      async (params: { id: string; sprintId: string }) => {
        this.project = await this.loadData(params.id, params.sprintId);
        // await this.loadData(params.id);
        this.showBars =
          this.project.showBars.sprint !== undefined
            ? this.project.showBars.sprint
            : this.showBars;
        this.checkboxes =
          this.project.spreadCheckboxes.sprint || this.checkboxes;
        this.radio = this.project.radio.sprint || this.radio;
        this.calcAgreement();
        this.calcRegression();
        this.createChartDataObjects();
        await this.planningPokerService.watchProject(params.id);
        if (this.planningPokerService.planningOpen) {
          const currentlyEstimatedFeature = this.sprint.features.filter(
            (x) => x._id === this.planningPokerService.currentFeatureId
          );
          if (currentlyEstimatedFeature.length > 0)
            currentlyEstimatedFeature[0].isBeingEstimated = true;
        }
        this.planningPokerService.estimationStarted.subscribe(
          (estimatedFeature) => {
            const currentlyEstimatedFeature = this.sprint.features.filter(
              (x) => x._id === this.planningPokerService.currentFeatureId
            );
            if (currentlyEstimatedFeature.length > 0)
              currentlyEstimatedFeature[0].isBeingEstimated = true;
          }
        );
        this.planningPokerService.estimationStopped.subscribe((_) => {
          this.sprint.features.forEach((x) => (x.isBeingEstimated = false));
          const currentlyEstimatedFeature = this.sprint.features.filter(
            (x) => x._id === this.planningPokerService.currentFeatureId
          );
          if (currentlyEstimatedFeature.length > 0)
            currentlyEstimatedFeature[0].isBeingEstimated = true;
        });
        this.changeDetector.detectChanges();
      }
    );
  }

  async loadData(projectId: string, sprintId: string): Promise<Project> {
    this.loading = true;
    var project = await this.projectService.getProjectByShortId(projectId);
    if (this.project) this.project.unsyncedChanges = project.unsyncedChanges;
    this.epics = project.epics = (
      await this.epicService.getEpicsByProjectId(project._id)
    ).sort((x, y) =>
      x.name.localeCompare(y.name, undefined, {
        sensitivity: "base",
        ignorePunctuation: true,
        usage: "sort",
      })
    );
    var sprint = await this.sprintService.getSprintByShortId(sprintId);
    this.fillEpicFilters(project.epics);
    let features = (
      await this.featureService.getFeaturesByProjectId(project._id)
    ).sort((x, y) => x.index - y.index);
    this.allFeatures = features;
    this.sprint = sprint;
    this.sprint["features"] = features.filter(
      (x) => x.sprint === this.sprint._id || (!x.sprint && !this.sprint._id)
    );
    var currentlyEstimatedFeature = features.filter(
      (x) => x._id === this.planningPokerService.currentFeatureId
    );
    if (currentlyEstimatedFeature.length > 0)
      currentlyEstimatedFeature[0].isBeingEstimated = true;

    let sprints = await this.sprintService.getSprintsByProjectId(project._id);
    this.sprints = [
      ...sprints.filter((x) => x.state === "active" && x._id !== sprint._id),
      ...sprints
        .filter((x) => x.state === "future" && x._id !== sprint._id)
        .sort((x, y) => y.index - x.index),
      ...sprints.filter((x) => x.state === "backlog" && x._id !== sprint._id),
    ];

    this.loading = false;
    return project;
  }

  // async reloadProject() {
  //   this.project = await this.projectService.getProjectByShortId(this.project.shortId);
  //   this.project.epics = await this.epicService.getEpicsByProjectId(this.project._id);
  // }

  /**
   * creates new feature with related projectID
   */
  async createItemClicked(newFeature) {
    newFeature.sprint = this.sprint._id;
    let createdFeature = await this.featureService.createFeature(newFeature);
    this.sprint.features.push(createdFeature);
    this.allFeatures.push(createdFeature);
    this.project.unsyncedChanges = (
      await this.projectService.getProjectByShortId(this.project.shortId)
    ).unsyncedChanges;
    this.changeDetector.detectChanges();
  }

  async createSprintClicked(newSprint) {
    newSprint.state = "future";
    let createdSprint = await this.sprintService.createSprint(newSprint);
    var sprints = (
      await this.sprintService.getSprintsByProjectId(this.project._id)
    ).sort((x, y) => x.index - y.index);
    this.project.sprints = sprints
      .filter((x) => x.state === "active")
      .concat(
        sprints
          .filter((x) => x.state === "future")
          .sort((x, y) => x.index - y.index)
      )
      .concat([
        {
          _id: null,
          name: "Backlog",
          shortId: null,
          completeDate: null,
          description: null,
          created_at: new Date(),
          index: 999,
          projectId: this.project._id,
          agirId: null,
          state: null,
        },
      ])
      .concat(
        sprints
          .filter((x) => x.state === "closed")
          .sort((x, y) => {
            return (
              new Date(y.completeDate).getTime() -
              new Date(x.completeDate).getTime()
            );
          })
      );
    this.project.unsyncedChanges = (
      await this.projectService.getProjectByShortId(this.project.shortId)
    ).unsyncedChanges;
    this.changeDetector.detectChanges();
  }

  /**
   * creates new feature with related projectID
   */
  async createThemeClicked(args) {
    if (args.newTheme._id === undefined) {
      let createdTheme = await this.epicService.createEpic(args.newTheme);
      this.project.epics.push(createdTheme);
      this.epics = this.project.epics = this.project.epics.sort((x, y) =>
        x.name.localeCompare(y.name, undefined, {
          sensitivity: "base",
          ignorePunctuation: true,
          usage: "sort",
        })
      );
      // await this.reloadProject();
      args.cbSubject.next(createdTheme);
      args.cbSubject.complete();
      this.project.unsyncedChanges = (
        await this.projectService.getProjectByShortId(this.project.shortId)
      ).unsyncedChanges;
      this.changeDetector.detectChanges();
    } else {
      await this.epicService.saveEpic(args.newTheme);
      // await this.reloadProject();
      args.cbSubject.next(args.newTheme);
      args.cbSubject.complete();
    }
  }

  /**
   * creates new epic with related projectID
   */
  async createEpic(newEpic) {
    try {
      console.log(newEpic._id);
      let epic = await this.epicService.createEpic(newEpic);
      this.project.epics.push(epic);
      this.epics = this.project.epics = this.project.epics.sort((x, y) =>
        x.name.localeCompare(y.name, undefined, {
          sensitivity: "base",
          ignorePunctuation: true,
          usage: "sort",
        })
      );
      this.newEpic = { color: Utils.presetThemeColors[0][0] } as Epic;
      this.project.unsyncedChanges = (
        await this.projectService.getProjectByShortId(this.project.shortId)
      ).unsyncedChanges;
      this.changeDetector.detectChanges();
    } catch (error) {
      this.newEpic = { color: Utils.presetThemeColors[0][0] } as Epic;
    }
    // await this.reloadProject();
  }

  fillEpicFilters(epics) {
    for (let i = 0; i < epics.length; i++) {
      epics[i].checked = false;
    }
  }

  /**
   * saves feature
   * @param feature
   */
  async saveFeature(feature) {
    await this.featureService.saveFeature(feature);
  }

  /**
   * returns all filtered epics as Array
   * @returns {Array}
   */
  getFilteredEpics() {
    let filteredEpics = [];
    for (let i = 0; i < this.project.epics.length; i++) {
      if (this.project.epics[i].filtered) {
        filteredEpics.push(this.project.epics[i]);
      }
    }
    return filteredEpics;
  }

  /**
   * changes filtered status of given theme and fits the filter name
   * @param epic
   */
  filterEpic() {
    this.epicFilterName = "";
    const filteredEpics = this.selectedThemes.map((x) => {
      if (!x) return { name: "Kein Theme" };
      else return this.project.epics.filter((y) => y._id === x)[0];
    });
    for (let i = 0; i < filteredEpics.length; i++) {
      if (i > 0) {
        this.epicFilterName += ", ";
      }
      this.epicFilterName += filteredEpics[i].name;
    }
    if (this.epicFilterName.length === 0) {
      this.epicFilterName = "Nach Themes filtern";
    }
  }

  /**
   * adds or removes the themeId to an array of all the selected Themes depending if it is already in the array.
   * Only the Items with an EpicId in this array are shown.
   * If the array is empty all items are shown.
   * @param themeId is the Id of the Theme that has been checked or unchecked.
   */
  selectTheme(themeId) {
    const index = this.selectedThemes.indexOf(themeId);
    if (index === -1) {
      this.selectedThemes.push(themeId);
    } else {
      this.selectedThemes.splice(index, 1);
    }
  }

  /**
   * returns number of estimated items
   * @returns {number}
   */
  getEstimatedItemCount(): Number {
    return this.sprint
      ? this.sprint.features.filter((x) => x.effort || x.effort === 0).length
      : 0;
  }

  getProjectEffort(): Number {
    return this.sprint
      ? this.sprint.features
          .filter((x) => x.effort)
          .map((x) => x.effort)
          .reduce((x, y) => x + y, 0)
      : 0;
  }

  async openNewItemDialog() {
    this.EditItemModal.modalComponent.open();
  }

  openNewThemeDialog() {
    this.EditThemeModal.modalComponent.open();
  }

  async syncProject() {
    let result = await (<ConfirmSyncComponent>(
      this.modalService.open(ConfirmSyncComponent).instance
    )).result
      .asObservable()
      .first()
      .toPromise();
    if (result === true) {
      this.loading = true;
      await this.JiraSyncProgressModal.sync(this.project.shortId);
      this.project = await this.loadData(
        this.project.shortId,
        this.sprint.shortId
      );
      this.toastService.show(
        "Erfolgreich synchronisiert!",
        4000,
        "confirm-toast"
      );
      this.changeDetector.detectChanges();
    }
  }

  async exportClicked() {
    var blob = await this.exportService.exportSprint(this.sprint._id);
    saveAs(
      blob,
      `${this.project.name.replace(
        /[\\/:"*?<>|]+/,
        ""
      )} ${this.sprint.name.replace(/[\\/:"*?<>|]+/, "")} Items.xlsx`
    );
  }

  async carouselDeleteItemClicked(feature: Feature) {
    var oldIndex = this.carouselItemIndex;
    this.carouselItemIndex = -1;
    var result = await this.DeleteItemConfirmationModal.getResultAsync();
    if (result === true) {
      await this.featureService.deleteFeatureById(feature._id);
      // this.projectFeatures.splice(this.projectFeatures.indexOf(feature), 1);
      this.sprint.features.splice(this.sprint.features.indexOf(feature), 1);
      this.project.unsyncedChanges = (
        await this.projectService.getProjectByShortId(this.project.shortId)
      ).unsyncedChanges;
      this.changeDetector.detectChanges();
    } else this.carouselItemIndex = oldIndex;
  }

  showFeatureDetails(i) {
    this.carouselItemIndex = i;
    this.changeDetector.detectChanges();
  }

  async carouselClosed() {
    this.carouselItemIndex = -1;
    for (var i = 0; i < this.sprint.features.length; i++)
      this.sprint.features[i].isBeingEstimated = false;
    var currentlyEstimatedFeature = this.sprint.features.filter(
      (x) => x._id === this.planningPokerService.currentFeatureId
    );
    if (
      currentlyEstimatedFeature.length > 0 &&
      this.planningPokerService.planningOpen
    )
      currentlyEstimatedFeature[0].isBeingEstimated = true;
    this.project.unsyncedChanges = (
      await this.projectService.getProjectByShortId(this.project.shortId)
    ).unsyncedChanges;
    this.calcAgreement();
    this.calcRegression();
    this.createChartDataObjects();
    this.changeDetector.detectChanges();
  }

  async itemDeleteClicked(feature: Feature) {
    var result = await this.DeleteItemConfirmationModal.getResultAsync();
    if (result === true) {
      await this.featureService.deleteFeatureById(feature._id);
      this.sprint.features.splice(this.sprint.features.indexOf(feature), 1);
      this.project.unsyncedChanges = (
        await this.projectService.getProjectByShortId(this.project.shortId)
      ).unsyncedChanges;
      this.calcAgreement();
      this.calcRegression();
      this.createChartDataObjects();
      this.changeDetector.detectChanges();
    }
  }

  async itemEpicChanged(feature: Feature) {
    this.project.unsyncedChanges = (
      await this.projectService.getProjectByShortId(this.project.shortId)
    ).unsyncedChanges;
    this.changeDetector.detectChanges();
  }

  async itemMoveClicked(data: any) {
    if (
      await this.featureService.moveToSprint(data.feature._id, data.sprint._id)
    ) {
      this.sprint.features.splice(
        this.sprint.features.indexOf(data.feature),
        1
      );
      this.project.unsyncedChanges = (
        await this.projectService.getProjectByShortId(this.project.shortId)
      ).unsyncedChanges;
      this.calcAgreement();
      this.calcRegression();
      this.createChartDataObjects();
      this.changeDetector.detectChanges();
      this.toastService.show(
        "Item " +
          data.feature.agirId +
          " wurde in Sprint " +
          data.sprint.name +
          " verschoben",
        4000,
        "confirm-toast"
      );
    } else {
      this.toastService.show(
        "Fehler! Nicht Gespeichert",
        4000,
        "notification-toast"
      );
    }
  }

  async update(sprint) {
    try {
      await this.sprintService.saveSprint(sprint);
      this.project.unsyncedChanges = (
        await this.projectService.getProjectByShortId(this.project.shortId)
      ).unsyncedChanges;
      this.calcAgreement();
      this.calcRegression();
      this.createChartDataObjects();
      this.changeDetector.detectChanges();
    } catch (res) {
      console.log(res);
      this.toastService.show(
        "Fehler! Nicht Gespeichert",
        4000,
        "notification-toast"
      );
    }
  }

  async setNewIndex(event: any) {
    event.clone = false;
    var movedItem = this.sprint.features[event.newIndex];
    var movedItemGlobalIndex = this.allFeatures.indexOf(movedItem);
    var prevItemGlobalPosition = undefined;
    var nextItemGlobalPosition = undefined;
    if (event.newIndex == 0) {
      // move before (now) second item in list
      var nextItemGlobalIndex = this.allFeatures.indexOf(
        this.sprint.features[1]
      );
      nextItemGlobalPosition = this.allFeatures[nextItemGlobalIndex].index;
      prevItemGlobalPosition =
        nextItemGlobalIndex === 0
          ? 0
          : this.allFeatures[nextItemGlobalIndex - 1].index;
    } else {
      // move after (now) previous item in list
      var prevItemGlobalIndex = this.allFeatures.indexOf(
        this.sprint.features[event.newIndex - 1]
      );
      prevItemGlobalPosition = this.allFeatures[prevItemGlobalIndex].index;
      nextItemGlobalPosition =
        prevItemGlobalIndex === this.allFeatures.length - 1
          ? Math.max(...this.allFeatures.map((x) => x.index)) + 1
          : this.allFeatures[prevItemGlobalIndex + 1].index;
    }

    var newGlobalPosition =
      (prevItemGlobalPosition + nextItemGlobalPosition) / 2;
    this.sprint.features[event.newIndex].index = newGlobalPosition;
    this.allFeatures[movedItemGlobalIndex].index = newGlobalPosition;
    this.allFeatures = this.allFeatures.sort((x, y) => x.index - y.index);
    await this.featureService.saveFeature(this.sprint.features[event.newIndex]);
    this.project.unsyncedChanges = (
      await this.projectService.getProjectByShortId(this.project.shortId)
    ).unsyncedChanges;
    this.calcAgreement();
    this.calcRegression();
    this.createChartDataObjects();
    this.changeDetector.detectChanges();
  }

  getEpicCount(): number {
    let epics = this.sprint["features"].map((x) => x.epicId).filter((x) => x);
    return epics.filter((x, i) => epics.indexOf(x) === i).length;
  }

  /*
Generates the Dataobjects for the combo chart
*/
  createChartDataObjects() {
    this.bars = [];
    this.lines = [];

    if (this.sprint.features.length > 0) {
      if (this.sprint.features.length > 39) {
        this.chartWidth = this.sprint.features.length * 30 + 500 + "px";
      } else {
        this.chartWidth = "inherit";
      }
      var tempAgreementValue = [];
      var tempAgreementAverageValue = [];
      var tempAgreementMedianValue = [];
      var tempTrendValue = [];
      this.sprint.features.forEach((feature, index) => {
        if (feature.effort || feature.estimations.length > 0) {
          if (
            feature.estimations[feature.estimations.length - 1] === undefined
          ) {
            var barValues = [feature.effort, feature.effort, feature.effort];
          } else {
            var barValues = [
              feature.estimations[feature.estimations.length - 1].average || 0,
              feature.estimations[feature.estimations.length - 1].median || 0,
              feature.effort || 0,
            ];
          }
          if (this.showBars) {
            var tempBarValue = {
              name: feature.agirId,
              value: barValues[this.radio - 1],
            };
            this.bars.push(tempBarValue);
          } else {
            var tempBarValue2 = {
              name: feature.agirId,
              value: 0,
            };
            this.bars.push(tempBarValue2);
          }

          if (this.checkboxes[0]) {
            let tempLineSeriesValue = {
              name: feature.agirId,
              value:
                feature.estimations[feature.estimations.length - 1] ===
                undefined
                  ? 0
                  : feature.estimations[feature.estimations.length - 1]
                      .agreement,
            };
            tempAgreementValue.push(tempLineSeriesValue);
          }
          if (this.checkboxes[1]) {
            let tempLineSeriesValue = {
              name: feature.agirId,
              value: this.agreementAverage,
            };
            tempAgreementAverageValue.push(tempLineSeriesValue);
          }
          if (this.checkboxes[2]) {
            let tempLineSeriesValue = {
              name: feature.agirId,
              value: this.agreementMedian,
            };
            tempAgreementMedianValue.push(tempLineSeriesValue);
          }
          if (this.checkboxes[3]) {
            let tempLineSeriesValue = {
              name: feature.agirId,
              value: this.m * (tempTrendValue.length + 1) + this.b,
            };
            // console.log("m= " + this.m);
            // console.log("b= " + this.b);
            // console.log("x= " + (tempTrendValue.length + 1));
            // console.log(tempLineSeriesValue);

            tempTrendValue.push(tempLineSeriesValue);
          }
        }
      });
      if (tempAgreementValue.length > 0) {
        this.lines.push({
          name: "Streuung",
          series: tempAgreementValue,
        });
      }
      if (tempAgreementAverageValue.length > 0) {
        this.lines.push({
          name: "Streuung Mittelwert",
          series: tempAgreementAverageValue,
        });
      }
      if (tempAgreementMedianValue.length > 0) {
        this.lines.push({
          name: "Streuung Median",
          series: tempAgreementMedianValue,
        });
      }
      if (tempTrendValue.length > 0) {
        this.lines.push({
          name: "Streuung Trend",
          series: tempTrendValue,
        });
      }
    }
    window.dispatchEvent(new Event("resize"));
  }
  /**
   * calculates the median and average from agreements
   */
  calcAgreement() {
    this.agreementAverage = 0;
    this.agreementValues = [];
    if (this.sprint.features.length > 0) {
      this.sprint.features.forEach((feature) => {
        if (feature.estimations.length > 0) {
          //&& variant.estimations[variant.estimations.length - 1].agreement
          this.agreementValues.push(
            feature.estimations[feature.estimations.length - 1].agreement || 0.0
          );
          this.agreementAverage +=
            feature.estimations[feature.estimations.length - 1].agreement ||
            0.0;
        }
      });
      if (this.agreementAverage > 0) {
        this.agreementAverage /= this.agreementValues.length;

        if (this.agreementValues.length % 2 === 1) {
          this.agreementMedian = this.agreementValues[
            Math.ceil(this.agreementValues.length / 2) - 1
          ];
        } else {
          this.agreementMedian =
            (this.agreementValues[this.agreementValues.length / 2 - 1] +
              this.agreementValues[this.agreementValues.length / 2]) /
            2;
        }
      }
    }
  }

  /**
   * calculates the trendline for agreement
   */
  calcRegression() {
    this.m = 0;
    this.b = 0;
    var featureAverage = 0;
    if (this.agreementValues.length > 0) {
      this.agreementValues.forEach((value, index) => {
        featureAverage += index + 1;
      });
      featureAverage /= this.agreementValues.length;
    }
    var sumA = 0;
    var sumB = 0;
    if (this.agreementValues.length > 0) {
      this.agreementValues.forEach((value, index) => {
        sumA += (index + 1 - featureAverage) * (value - this.agreementAverage);
        sumB += Math.pow(index + 1 - featureAverage, 2);
      });
      this.m = sumA / sumB;
      this.b = this.agreementAverage - this.m * featureAverage;
    }
  }

  radioChanged() {
    this.project.radio.sprint = this.radio;
    // this.projectService.updateProject(this.project);
    var barLabels = [
      "Story Points Mittelwert",
      "Story Points Median",
      "Story Points Einigung",
    ];
    this.yAxisLabelBar = barLabels[this.radio - 1];
    this.createChartDataObjects();
  }

  checkboxChanged() {
    this.project.showBars.sprint = this.showBars;
    this.project.spreadCheckboxes.sprint = this.checkboxes;
    // this.projectService.updateProject(this.project);
    var count = 0;
    this.checkboxes.forEach((value) => {
      if (value === true) {
        count++;
      }
    });
    if (count === 0 && !this.showBars) {
      this.bars = [];
      this.lines = [];
    } else {
      this.createChartDataObjects();
    }
  }
}



// WEBPACK FOOTER //
// /agir/src/app/components/feature-canvas/feature-canvas.component.ts